/**
 * Карточка поставщика: снятые у него камни и комментарий к выбранным.
 * Читается из локальной базы, поэтому работает и без сети.
 */
import { useEffect, useRef, useState } from 'react';
import {
  db, enqueue,
  type LocalSupplier, type LocalStone, type LocalMedia,
} from '../db';
import { apiFetch, syncNow } from '../sync';
import { VoiceRecorder } from '../lib/media';
import { pictureUrl, releasePictures } from '../lib/pictures';
import { ulid, STATUS_LABEL, plural, parseMoney, formatMoney, type Status } from '@core';
import { loadRows } from '../lib/catalog';
import { loadRates, convert, ratesAsOf, HANDY_CURRENCIES, type Rates } from '../lib/rates';
import { MergeSheet } from './MergeSheet';
import { SupplierTalks } from './Talks';
import { SupplierTasks } from './Tasks';
import { whereIs } from '../lib/where';
import { rowPerCt, rowCurrency, type StoneRow } from '@core';

/**
 * Цифры поставщика — из уже накопленного, без сервера.
 *
 * Это не отчётность, а память перед подходом к стенду: сколько смотрели,
 * что он вообще возит и в каких деньгах. Специализация выводится из его же
 * камней — «в основном шпинель» скажет больше, чем любая анкета.
 */
function SupplierDigits({ rows }: { rows: StoneRow[] }) {
  const [rates, setRates] = useState<Rates | null>(null);
  /** В какой валюте смотрим сейчас. Пусто — в основной валюте стенда. */
  const [view, setView] = useState('');
  useEffect(() => { void loadRates().then(setRates); }, []);

  if (rows.length < 2) return null;

  const shortlisted = rows.filter((r) => ['interesting', 'strong', 'priority'].includes(r.status)).length;
  const bought = rows.filter((r) => r.status === 'purchased').length;

  const types = new Map<string, number>();
  for (const r of rows) {
    const t = r.values.get('stone_type')?.text;
    if (t) types.set(t, (types.get(t) ?? 0) + 1);
  }
  const top = [...types.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2);
  const special = top.length && top[0][1] >= Math.max(2, rows.length * 0.4)
    ? top.map(([t]) => t).join(', ') : null;

  /*
   * Обычная цена этого поставщика — медиана по его камням.
   *
   * Основная валюта стенда — та, в которой он называет цены чаще. Остальные
   * приводим к ней по курсу: без этого медиана из батов и долларов вместе —
   * число, которого не существует. Что пересчитано, а что названо, видно
   * подписью, и нажатием валюта переключается.
   */
  const priced = rows.filter((r) => rowPerCt(r) != null);
  const byCur = new Map<string, number[]>();
  for (const r of priced) {
    const c = rowCurrency(r);
    byCur.set(c, [...(byCur.get(c) ?? []), rowPerCt(r)!]);
  }
  const main = [...byCur.entries()].sort((a, b) => b[1].length - a[1].length)[0];
  const base = main?.[0] ?? 'USD';
  const cur = view || base;
  const mixed = byCur.size > 1;

  // Пересчитываем всё в валюту показа. Камни, для которых курса нет, в
  // медиану не попадают — лучше меньше чисел, чем неверные.
  const all: number[] = [];
  for (const [c, list] of byCur) {
    for (const v of list) {
      const x = c === cur ? v : convert(v, c, cur, rates);
      if (x != null) all.push(x);
    }
  }
  all.sort((a, b) => a - b);
  const mid = all.length ? all[Math.floor(all.length / 2)] : null;
  const converted = mixed || cur !== base;

  const money = (n: number) => {
    const m = parseMoney(Math.round(n), cur);
    return m ? formatMoney(m) : String(n);
  };

  /** По нажатию — следующая валюта: сначала те, что есть у стенда, потом ходовые. */
  const ring = [...new Set([base, ...byCur.keys(), ...HANDY_CURRENCIES])];
  const nextCur = () => setView(ring[(ring.indexOf(cur) + 1) % ring.length]);

  return (
    <>
      <div className="tally" style={{ paddingTop: 10 }}>
        <div className="on"><b>{rows.length}</b><span>снято</span></div>
        <div className={shortlisted ? 'on' : ''}><b>{shortlisted}</b><span>отобрано</span></div>
        {bought > 0 && <div className="on"><b>{bought}</b><span>куплено</span></div>}
        {mid != null && (
          <div className="on tappable" onClick={nextCur} role="button">
            <b>{money(mid)}</b>
            <span>обычная цена /ct{converted ? ' · пересчёт' : ''}</span>
          </div>
        )}
      </div>
      {special && (
        <p className="meta" style={{ marginTop: 0, marginBottom: 10 }}>
          В основном возит: <b>{special}</b>
        </p>
      )}
      {converted && (
        <p className="meta" style={{ marginTop: 0, marginBottom: 10 }}>
          {mixed
            ? <>Цены названы в разных валютах ({[...byCur.keys()].join(', ')}) и приведены
                к <b>{cur}</b> по курсу {ratesAsOf(rates)}.</>
            : <>Названо в {base}, показано в <b>{cur}</b> по курсу {ratesAsOf(rates)}.</>}
          {' '}Нажмите на число, чтобы сменить валюту.
        </p>
      )}
    </>
  );
}

// ── карточка поставщика ─────────────────────────────────────────────────────

export function SupplierView({ supplierId, onBack, onCapture, onOpenStone, onEdit, onMerged, onTalk, onOpenTalk }: {
  supplierId: string;
  onBack: () => void;
  onCapture: (kind: 'stone' | 'parcel') => void;
  onOpenStone: (id: string) => void;
  onEdit?: () => void;
  /** Стенд влит в другой — показать тот, в который влили. */
  onMerged?: (targetId: string) => void;
  /** Беседа с этим стендом через переводчика. */
  onTalk?: () => void;
  onOpenTalk?: (id: string) => void;
}) {
  const [supplier, setSupplier] = useState<LocalSupplier | null>(null);
  const [stones, setStones] = useState<LocalStone[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [rows, setRows] = useState<StoneRow[]>([]);
  const [merging, setMerging] = useState(false);
  const recorder = useRef(new VoiceRecorder());

  useEffect(() => {
    void (async () => {
      setSupplier((await db.suppliers.get(supplierId)) ?? null);
      const list = await db.stones.where('supplierId').equals(supplierId).reverse().sortBy('createdAt');
      setStones(list);
      setRows((await loadRows()).filter((r) => r.supplierId === supplierId));

      const map: Record<string, string> = {};
      for (const s of list.slice(0, 60)) {
        const m: LocalMedia | undefined = await db.media
          .where('subjectId').equals(s.id).filter((x) => x.role === 'preview').first();
        const u = pictureUrl(m);
        if (u) map[s.id] = u;
      }
      setThumbs(map);
    })();
    return () => releasePictures();
  }, [supplierId]);

  /**
   * Убрать стенд.
   *
   * Только пустой: у стенда с камнями убирание осиротило бы их. Двойника с
   * камнями не убирают, а склеивают с настоящим стендом — кнопка рядом.
   */
  async function removeSupplier() {
    if (!supplier || stones.length > 0) return;
    if (!window.confirm(
      `Убрать стенд «${supplier.companyName}»?\n\nКамней у него нет, поэтому ничего не потеряется. Запись останется на сервере — вернуть можно.`)) return;
    await db.suppliers.delete(supplier.id);
    await enqueue('supplier.remove', { id: supplier.id }, ulid());
    void syncNow();
    onBack();
  }

  /** Пометка «вернуться завтра» живёт на главном экране, пока её не снимут. */
  async function toggleFollowUp() {
    if (!supplier) return;
    const next = !supplier.followUp;
    await db.suppliers.update(supplier.id, { followUp: next });
    setSupplier({ ...supplier, followUp: next });
    await enqueue('supplier.upsert', {
      id: supplier.id, companyName: supplier.companyName, followUp: next,
    }, ulid());
    void syncNow();
  }

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  /** Комментарий к нескольким камням сразу: «вся эта группа из Мозамбика». */
  async function groupNote() {
    if (!recording) {
      try {
        await recorder.current.start();
        setRecording(true);
      } catch {
        setToast('Микрофон недоступен');
      }
      return;
    }

    setRecording(false);
    setBusy(true);
    const out = await recorder.current.stop();
    if (!out) { setBusy(false); return; }

    const ids = [...picked];
    const anchor = ids[0];
    const mediaId = ulid();
    const voiceId = ulid();
    const now = new Date().toISOString();

    await db.media.put({ id: mediaId, subjectId: anchor, role: 'audio', blob: out.blob,
      mime: out.mime, bytes: out.blob.size, uploaded: false, createdAt: now });
    await db.voices.put({ id: voiceId, subjectId: anchor, subjectKind: 'group', subjectIds: ids,
      mediaId, status: 'pending', durationMs: out.durationMs, createdAt: now });
    await enqueue('voice.add', { id: voiceId, subjectId: anchor, subjectKind: 'group',
      subjectIds: ids, mediaId, durationMs: out.durationMs }, ulid());

    if (navigator.onLine) {
      try {
        const fd = new FormData();
        fd.append('file', out.blob, `note.${out.mime.includes('mp4') ? 'mp4' : 'webm'}`);
        const tr = await apiFetch('/api/transcribe', { method: 'POST', body: fd });
        if (tr.ok) {
          const { text } = (await tr.json()) as { text: string };
          await db.voices.update(voiceId, { transcript: text, status: 'done' });
          await enqueue('voice.add', { id: voiceId, subjectId: anchor, subjectKind: 'group',
            subjectIds: ids, mediaId, transcript: text }, ulid());
        }
      } catch { /* разберётся при синхронизации */ }
    }

    setBusy(false);
    setPicked(new Set());
    setToast(`Заметка сохранена к ${ids.length} ${plural(ids.length, 'камню', 'камням', 'камням')}`);
    setTimeout(() => setToast(''), 2600);
    void syncNow();
  }

  if (!supplier) return <div className="app"><div className="blankslate">Поставщик не найден.</div></div>;

  const selecting = picked.size > 0;
  const local = supplier.companyNameLocal;

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Выставка</button>
        <h1 className="t">{supplier.companyName}</h1>
        <p className="meta">
          {local && <>{local}<span className="sep">·</span></>}
          {[whereIs(supplier), supplier.city].filter(Boolean).join(' · ')}
        </p>
      </div>

      <div className="scroll">
        <div className="states" style={{ marginBottom: 4 }}>
          <button className="state-btn" aria-pressed={Boolean(supplier.followUp)}
                  onClick={() => void toggleFollowUp()}>
            {supplier.followUp ? 'Вернуться завтра ✓' : 'Вернуться завтра'}
          </button>
          {/* Стенд меняется на второй день, в номере теряется цифра — без
              правки человек заводит двойника вместо исправления. */}
          {onTalk && <button className="state-btn" onClick={onTalk}>Беседа</button>}
          {onEdit && <button className="state-btn" onClick={onEdit}>Править стенд</button>}
          {onMerged && <button className="state-btn" onClick={() => setMerging(true)}>Склеить с двойником</button>}
        </div>

        <SupplierDigits rows={rows} />

        {supplier.contacts.length > 0 && (
          <div className="states">
            {supplier.contacts.map((c) => (
              <a key={c.id} className="state-btn" style={{ textDecoration: 'none' }}
                 href={c.kind === 'email' ? `mailto:${c.value}` : c.kind === 'phone' ? `tel:${c.value}` : undefined}>
                {c.kind === 'phone' ? 'Позвонить' : c.kind === 'email' ? 'Написать' : c.value}
              </a>
            ))}
          </div>
        )}

        {/* Записи стенда: руками из правки и находки, добавленные из бесед. */}
        {supplier.notes?.trim() && (
          <>
            <div className="head"><h3>Записи</h3></div>
            <div className="memo supplier-notes">{supplier.notes.trim()}</div>
          </>
        )}
        <SupplierTasks supplierId={supplierId} onOpenTalk={onOpenTalk} />
        {onOpenTalk && <SupplierTalks supplierId={supplierId} onOpen={onOpenTalk} />}

        <div className="head">
          <h3>{selecting ? `Выбрано ${picked.size}` : 'Снятые камни'}</h3>
          <span>{selecting ? <button className="crumb" style={{ padding: 0 }} onClick={() => setPicked(new Set())}>снять выбор</button> : stones.length}</span>
        </div>

        {stones.length === 0 ? (
          <>
            <div className="blankslate">
              <b>Пока ничего не снято</b>
              Нажмите «Снимать камни» — камера откроется сразу, и каждый кадр
              сам получит номер.
            </div>
            {/* Пустой стенд чаще всего заведён по ошибке — вторым заходом
                на визитку того же поставщика. Даём его убрать. */}
            <button className="btn btn-secondary" style={{ color: 'var(--alert)' }}
                    onClick={() => void removeSupplier()}>
              Убрать этот стенд
            </button>
          </>
        ) : (
          <>
            <div className="shelf">
              {stones.map((s) => {
                const no = (s.publicNo ?? s.localNo).split('-').slice(-1)[0];
                const on = picked.has(s.id);
                return (
                  <button
                    key={s.id}
                    className="stone"
                    data-picked={on ? '1' : '0'}
                    onClick={() => (selecting ? toggle(s.id) : onOpenStone(s.id))}
                    onContextMenu={(e) => { e.preventDefault(); toggle(s.id); }}
                    aria-label={`${no}, ${STATUS_LABEL[s.status as Status] ?? s.status}`}
                  >
                    {thumbs[s.id]
                      ? <img src={thumbs[s.id]} alt="" />
                      : <span className="blank">{s.kind === 'parcel' ? '⁘' : '◆'}</span>}
                    {s.status !== 'viewed' && <span className={`state st-${s.status}`} />}
                    <span className="tag">{no}</span>
                    {on && <span className="pick">✓</span>}
                  </button>
                );
              })}
            </div>
            {!selecting && (
              <p className="meta" style={{ marginTop: 12 }}>
                Принесли ещё камни или лот — жмите «Снимать камни» внизу, они лягут
                сюда же, к этому стенду. Заводить его заново не нужно.
                <br />Долгое нажатие на кадре — выбрать несколько и надиктовать общий комментарий.
              </p>
            )}
          </>
        )}
      </div>

      {toast && <div className="pill">{toast}</div>}

      {merging && onMerged && (
        <MergeSheet supplier={supplier} onClose={() => setMerging(false)}
                    onMerged={(id) => { setMerging(false); onMerged(id); }} />
      )}

      <div className="dock">
        {selecting ? (
          <button className={`btn ${recording ? 'btn-secondary' : 'btn-primary'}`} disabled={busy}
                  onClick={() => void groupNote()}>
            {busy ? 'Сохраняю…' : recording ? 'Остановить запись' : `Комментарий к ${picked.size}`}
          </button>
        ) : (
          <>
            <button className="btn btn-primary" onClick={() => onCapture('stone')}>Снимать камни</button>
            <button className="btn btn-secondary" onClick={() => onCapture('parcel')}>Партию</button>
          </>
        )}
      </div>
    </div>
  );
}
